import { useCallback, useMemo, Fragment } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { View } from 'foundations-components/transitional/components/View';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Select } from 'foundations-components/transitional/components/Select';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { getIndustryOptions, getEmployeesOptions } from '../utils/options';
import { FILTER_KEY, FILTER, INDUSTRY_KEY, EMPLOYEES_KEY, SERVICE_KEY, SORT_KEY, SORT } from '../constants/ReviewsListQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
export default function ReviewsFilterSortDropdowns({
  catalogServices,
  excludedFilterOptions,
  onUpdateFilters,
  reviewFilters,
  reviewStats,
  showDivider = true
}) {
  const {
    display
  } = useResponsiveProps({
    display: {
      xs: 'block',
      md: 'inline-block'
    }
  });
  const {
    buttonUse,
    marginInlineStart
  } = useResponsiveProps({
    buttonUse: {
      xs: 'link',
      lg: 'transparent'
    },
    // NOTE: using `!important` due to `Select` has some built-in margin for `Selects` that are close to each other
    marginInlineStart: {
      xs: '400 !important',
      lg: '0 !important'
    }
  });
  const validFilters = useMemo(() => reviewStats.getValidRatingFilters(), [reviewStats]);
  const buildDropdown = useCallback((textKey, test) => {
    const options = Object.keys(test).reduce((result, option) => {
      if (!excludedFilterOptions || !excludedFilterOptions.includes(option)) {
        result.push({
          text: I18n.text(`marketplace-ui-reviews.list.${textKey}.${option}`),
          value: option,
          disabled: textKey === FILTER_KEY && option !== FILTER.ALL && !validFilters.has(option)
        });
      }
      return result;
    }, []);
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(View, {
        display: display,
        children: /*#__PURE__*/_jsx(Text, {
          typography: "body-125",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-reviews.list.${textKey}.label`
          })
        })
      }), /*#__PURE__*/_jsx(Select, {
        buttonUse: buttonUse,
        options: options,
        menuWidth: 250,
        marginInlineStart: marginInlineStart,
        display: "inline-block",
        value: reviewFilters.get(textKey),
        onChange: ({
          target: {
            value
          }
        }) => onUpdateFilters({
          textKey,
          value,
          source: 'dropdown filter'
        }),
        "data-test-id": `${textKey}-reviews-list-select`,
        valueComponent: ValueComponent
      }, textKey)]
    });
  }, [buttonUse, marginInlineStart, display, excludedFilterOptions, onUpdateFilters, reviewFilters, validFilters]);
  const renderedFilterDropdown = buildDropdown(FILTER_KEY, FILTER);
  const renderedSortDropdown = buildDropdown(SORT_KEY, SORT);
  const industryOptions = useMemo(getIndustryOptions, []);
  const renderedIndustryDropdown = /*#__PURE__*/_jsx(Select, {
    buttonUse: buttonUse,
    placeholder: I18n.text('marketplace-ui-reviews.industry.placeholder'),
    options: industryOptions,
    menuWidth: 250,
    marginInlineStart: marginInlineStart,
    display: "inline-block",
    value: reviewFilters.get(INDUSTRY_KEY),
    onChange: ({
      target: {
        value = ''
      }
    }) => onUpdateFilters({
      textKey: INDUSTRY_KEY,
      value,
      source: 'dropdown filter'
    }),
    clearable: true,
    "data-test-id": "industry-reviews-list-select",
    valueComponent: ValueComponent
  }, INDUSTRY_KEY);
  const employeesOptions = useMemo(getEmployeesOptions, []);
  const renderedEmployeesDropdown = /*#__PURE__*/_jsx(Select, {
    buttonUse: buttonUse,
    placeholder: I18n.text('marketplace-ui-reviews.employees.placeholder'),
    options: employeesOptions,
    menuWidth: 250,
    marginInlineStart: marginInlineStart,
    display: "inline-block",
    value: reviewFilters.get(EMPLOYEES_KEY),
    onChange: ({
      target: {
        value = ''
      }
    }) => onUpdateFilters({
      textKey: EMPLOYEES_KEY,
      value,
      source: 'dropdown filter'
    }),
    clearable: true,
    "data-test-id": "employees-reviews-list-select",
    valueComponent: ValueComponent
  }, EMPLOYEES_KEY);
  const renderedCatalogServicesDropdown = catalogServices && /*#__PURE__*/_jsx(Select, {
    buttonUse: buttonUse,
    placeholder: I18n.text('marketplace-ui-reviews.services.placeholder'),
    options: catalogServices.map(({
      id,
      name
    }) => ({
      value: id,
      text: name
    })),
    menuWidth: 250,
    marginInlineStart: marginInlineStart,
    display: "inline-block",
    value: reviewFilters.get(SERVICE_KEY.toLowerCase()),
    onChange: ({
      target: {
        value = ''
      }
    }) => onUpdateFilters({
      textKey: SERVICE_KEY,
      value,
      source: 'dropdown filter'
    }),
    clearable: true,
    "data-test-id": "services-reviews-list-select",
    valueComponent: ValueComponent
  }, SERVICE_KEY);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(Grid, {
      children: [/*#__PURE__*/_jsxs(GridItem, {
        size: {
          xs: 12,
          xl: catalogServices ? 12 : 9
        },
        children: [renderedFilterDropdown, renderedIndustryDropdown, renderedEmployeesDropdown, renderedCatalogServicesDropdown]
      }), /*#__PURE__*/_jsx(GridItem, {
        size: {
          xs: 12,
          xl: catalogServices ? 12 : 3
        },
        children: renderedSortDropdown
      })]
    }), showDivider && /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    })]
  });
}